/**
 * HubLibraryList — workspace-level library index inside the slide-
 * over Hub. Sister to ``HubList.tsx`` but lists ALL libraries in the
 * workspace rather than the docs in one library.
 *
 * Layout (restrained, one accent colour):
 *   - Sticky header: workspace switcher, "Source library" title with a
 *     "+ New library" button, a one-paragraph guidance note, search.
 *   - One bordered list; rows separated by hairlines. Each row has a
 *     radio dot (filled on the chosen library), the name, an optional
 *     description, and a meta line. A "›" button opens the library to
 *     look at (or upload to) its documents without choosing it.
 *
 * Guidance note — the onboarding step before drafting:
 *   - No libraries: create one and upload source documents.
 *   - Only empty libraries (a new account's personal library): upload
 *     into it, or create a new one.
 *   - Otherwise: choose the library that holds this document's sources.
 *
 * Like a file picker (Google Picker, OneDrive, Dropbox Chooser), a row
 * click only marks the library (``onPickLibrary``); the slide-over's footer
 * commits it. The mark survives a trip into a library and back.
 *
 * Search text and scroll position can be owned by the parent
 * (``searchTerm`` / ``onSearchChange``, ``scrollTop`` /
 * ``onScrollTopChange``) so they survive a trip into a library and back.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Library, Workspace } from "../../../types";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";

export interface HubLibraryListProps {
  /** All libraries in the writer's workspace. */
  libraries: Library[];
  /** The marked library, if any — its row gets a filled radio dot and
   *  ``aria-current``. */
  activeLibraryId: string | null;
  /** Click on a row body → mark the library (the slide-over footer commits
   *  it; a bare list with no footer can treat this as the pick). */
  onPickLibrary: (libraryId: string) => void;
  /** Click on the right chevron → inspect the library inside the Hub. */
  onInspectLibrary: (libraryId: string) => void;
  /** Create new library action — typically opens the slide-over in
   *  ``creating`` mode. */
  onCreateLibrary?: () => void;
  /** Workspaces available to the writer. When provided (2+), a switcher
   *  renders atop the header so the writer can change workspace without
   *  leaving the picker. Omit to hide the switcher entirely. */
  workspaces?: Workspace[];
  /** The active workspace id — drives the switcher's selected option. */
  selectedWorkspaceId?: string | null;
  /** Fires when the writer picks a different workspace. The parent
   *  persists it and cascades the library re-fetch (the ``libraries``
   *  prop updates in place while this list stays mounted). */
  onSelectWorkspace?: (workspaceId: string) => void;
  /** Known page totals per library id (sum of ``DocumentInfo.page_count``
   *  via ``totalPageCount``). Usually only the inspected library has an
   *  inventory loaded; libraries without an entry (or ``null``) simply
   *  omit the "· M pages" part of the meta line. */
  libraryPageCounts?: Record<string, number | null | undefined>;
  /** Controlled search text. Omit to let the list own it (it is then lost
   *  when the list unmounts, e.g. on a trip into a library). */
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  /** Controlled scroll position of the list, restored on mount. */
  scrollTop?: number;
  onScrollTopChange?: (value: number) => void;
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: surfaceColors.paper,
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: surfaceColors.paper,
    padding: "16px 14px 10px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    flexShrink: 0,
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  workspaceRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  // One workspace: nothing to switch, so a single quiet line.
  workspaceRowInline: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: "6px",
  },
  workspaceLabel: {
    fontSize: "11px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink4,
    userSelect: "none",
  },
  workspaceSelect: {
    width: "100%",
    padding: "6px 8px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    backgroundColor: "var(--surface-raised)",
    cursor: "pointer",
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-1px",
    },
  },
  workspaceStatic: {
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink2,
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  headerTitle: {
    fontSize: "15px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink,
    margin: 0,
  },
  createBtn: {
    backgroundColor: "transparent",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "4px",
    cursor: "pointer",
    padding: "4px 9px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink2,
    whiteSpace: "nowrap",
    ":hover": {
      borderTopColor: inkColors.ink4,
      borderRightColor: inkColors.ink4,
      borderBottomColor: inkColors.ink4,
      borderLeftColor: inkColors.ink4,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
  // Guidance note: plain text with a thin left rule, no fill.
  guide: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    paddingLeft: "10px",
    borderLeft: `2px solid ${surfaceColors.lineStrong}`,
  },
  guideText: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: inkColors.ink2,
    fontFamily: fonts.body,
    margin: 0,
  },
  guideActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  guidePrimary: {
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    border: "none",
    borderRadius: "4px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    cursor: "pointer",
    ":hover": { opacity: 0.92 },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  guideSecondary: {
    backgroundColor: "var(--surface-raised)",
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "4px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    cursor: "pointer",
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "7px 10px",
    fontSize: "12px",
    fontFamily: fonts.body,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "4px",
    backgroundColor: "var(--surface-raised)",
    color: inkColors.ink,
    outline: "none",
    "::placeholder": {
      color: inkColors.ink4,
    },
    ":focus": {
      borderTopColor: inkColors.ink3,
      borderRightColor: inkColors.ink3,
      borderBottomColor: inkColors.ink3,
      borderLeftColor: inkColors.ink3,
    },
  },
  listContainer: {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
    padding: "12px 14px 16px",
  },
  list: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    backgroundColor: "var(--surface-raised)",
    overflow: "hidden",
  },
  countLine: {
    fontSize: "11px",
    color: inkColors.ink4,
    fontFamily: fonts.body,
    margin: "0 0 6px 2px",
  },
  empty: {
    padding: "24px 12px",
    textAlign: "center",
    fontSize: "12px",
    color: inkColors.ink4,
    fontFamily: fonts.body,
  },
  rowWrap: {
    display: "flex",
    alignItems: "stretch",
    width: "100%",
    boxSizing: "border-box",
    borderTop: `1px solid ${surfaceColors.line}`,
    ":first-child": {
      borderTopColor: "transparent",
    },
    ":hover": {
      backgroundColor: surfaceColors.paper,
    },
  },
  rowBody: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    flex: 1,
    minWidth: 0,
    padding: "10px 4px 10px 12px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: fonts.body,
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
    },
  },
  // Radio dot: hollow ring; the chosen library gets a filled centre.
  radio: {
    width: "14px",
    height: "14px",
    marginTop: "1px",
    borderRadius: "50%",
    border: `1.5px solid ${inkColors.ink5}`,
    boxSizing: "border-box",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOn: {
    borderTopColor: brandColors.primary,
    borderRightColor: brandColors.primary,
    borderBottomColor: brandColors.primary,
    borderLeftColor: brandColors.primary,
    "::after": {
      content: '""',
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      backgroundColor: brandColors.primary,
    },
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  rowNameLine: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    minWidth: 0,
  },
  // Long names (e.g. "VEOZA 2693-CL-0301 (SKYLIGHT 1) - CSR source…")
  // wrap to two lines before ellipsing; full name on hover.
  rowName: {
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.3,
    color: inkColors.ink,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflowWrap: "anywhere",
    minWidth: 0,
  },
  rowNameOn: {
    fontWeight: 700,
  },
  scopeTag: {
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    flexShrink: 0,
  },
  // Same two-line clamp as the name; full description on hover.
  rowSubtitle: {
    fontSize: "11px",
    lineHeight: 1.35,
    color: inkColors.ink3,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflowWrap: "anywhere",
    minWidth: 0,
  },
  rowMeta: {
    fontSize: "11px",
    fontFamily: fonts.body,
    color: inkColors.ink4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowMetaEmpty: {
    color: inkColors.ink3,
  },
  rowUploadBtn: {
    alignSelf: "center",
    flexShrink: 0,
    margin: "0 8px 0 4px",
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    border: "none",
    borderRadius: "4px",
    padding: "5px 10px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    cursor: "pointer",
    ":hover": { opacity: 0.92 },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  rowChevronBtn: {
    backgroundColor: "transparent",
    border: "none",
    borderLeft: `1px solid ${surfaceColors.line}`,
    cursor: "pointer",
    padding: "0 10px",
    minWidth: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: inkColors.ink4,
    fontSize: "20px",
    lineHeight: 1,
    ":hover": {
      color: inkColors.ink,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
    },
  },
});

/** True when the server reports zero processed documents. ``undefined``
 *  (older servers) is "unknown", never "empty". */
export function isEmptyLibrary(lib: Library): boolean {
  return lib.document_count === 0;
}

/**
 * Build the meta line for a library row.
 *
 * Displays: "X file(s) [· M pages] [· signed] [· modified DATE]",
 * or "Empty · add your source documents" when the library has no files.
 * Pages only when the parent supplied a known total (see
 * ``totalPageCount``) — never estimated.
 */
export function describeLib(lib: Library, pageCount?: number | null): string {
  if (isEmptyLibrary(lib)) return "Empty";
  const parts: string[] = [];

  const fileCount = lib.document_count ?? 0;
  parts.push(`${fileCount} file${fileCount === 1 ? "" : "s"}`);

  if (typeof pageCount === "number") {
    parts.push(`${pageCount.toLocaleString("en-US")} page${pageCount === 1 ? "" : "s"}`);
  }

  if (lib.signed) parts.push("signed");

  if (lib.modified) {
    parts.push(`modified ${lib.modified}`);
  }

  return parts.join(" · ");
}

/**
 * Filter libraries by search term — case-insensitive match on name or
 * description.
 */
function filterLibraries(libraries: Library[], searchTerm: string): Library[] {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return libraries;
  return libraries.filter(
    (lib) => lib.name.toLowerCase().includes(term) || (lib.description ?? "").toLowerCase().includes(term),
  );
}

/** Which onboarding note the header shows. */
export type LibraryGuidance = { kind: "none-yet" } | { kind: "all-empty"; only: Library | null } | { kind: "choose" };

export function libraryGuidance(libraries: Library[]): LibraryGuidance {
  if (libraries.length === 0) return { kind: "none-yet" };
  if (libraries.every(isEmptyLibrary)) {
    return { kind: "all-empty", only: libraries.length === 1 ? libraries[0] : null };
  }
  return { kind: "choose" };
}

export const HubLibraryList: React.FC<HubLibraryListProps> = ({
  libraries,
  activeLibraryId,
  onPickLibrary,
  onInspectLibrary,
  onCreateLibrary,
  workspaces,
  selectedWorkspaceId,
  onSelectWorkspace,
  libraryPageCounts,
  searchTerm: controlledSearch,
  onSearchChange,
  scrollTop,
  onScrollTopChange,
}) => {
  const styles = useStyles();
  const [localSearch, setLocalSearch] = useState("");
  const searchTerm = controlledSearch ?? localSearch;
  const setSearchTerm = (value: string) => {
    if (onSearchChange) onSearchChange(value);
    else setLocalSearch(value);
  };

  const filteredLibraries = useMemo(() => filterLibraries(libraries, searchTerm), [libraries, searchTerm]);
  const guidance = useMemo(() => libraryGuidance(libraries), [libraries]);

  // Put the list back where the writer left it before opening a library.
  const listRef = useRef<HTMLDivElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: restore once on mount
  useLayoutEffect(() => {
    if (listRef.current && scrollTop) listRef.current.scrollTop = scrollTop;
  }, []);

  const showWorkspaceSwitcher = !!workspaces && workspaces.length > 0 && !!onSelectWorkspace;
  const singleWorkspace = !!workspaces && workspaces.length === 1;
  const activeWorkspace = workspaces?.find((w) => w.id === selectedWorkspaceId) ?? null;
  const workspaceSwitcher = showWorkspaceSwitcher ? (
    <div
      className={mergeClasses(styles.workspaceRow, singleWorkspace && styles.workspaceRowInline)}
      data-testid="hub-workspace-switcher"
    >
      <div className={styles.workspaceLabel}>Workspace</div>
      {workspaces && workspaces.length > 1 ? (
        <select
          className={styles.workspaceSelect}
          value={selectedWorkspaceId ?? ""}
          onChange={(e) => {
            const id = e.currentTarget.value;
            if (id && id !== selectedWorkspaceId) onSelectWorkspace?.(id);
          }}
          data-testid="hub-workspace-select"
          aria-label="Switch workspace"
        >
          {workspaces.map((ws) => (
            <option key={ws.id} value={ws.id}>
              {ws.name}
            </option>
          ))}
        </select>
      ) : (
        <div className={styles.workspaceStatic}>{activeWorkspace?.name ?? "—"}</div>
      )}
    </div>
  ) : null;

  let guide: React.ReactNode;
  if (guidance.kind === "none-yet") {
    guide = (
      <div className={styles.guide} data-testid="hub-library-guide-none">
        <p className={styles.guideText}>
          The drafting agent works only from your source documents. Create a library and upload them: protocol, SAP,
          TLFs, prior reports.
        </p>
        {onCreateLibrary && (
          <div className={styles.guideActions}>
            <button type="button" className={styles.guidePrimary} onClick={onCreateLibrary}>
              Create a library
            </button>
          </div>
        )}
      </div>
    );
  } else if (guidance.kind === "all-empty") {
    const only = guidance.only;
    guide = (
      <div className={styles.guide} data-testid="hub-library-guide-empty">
        <p className={styles.guideText}>
          {only ? (
            <>
              <strong>{only.name}</strong> is empty. Press Upload on it below to add your source documents, or create a
              new library for this submission.
            </>
          ) : (
            <>Your libraries are empty. Press Upload on the one you want to fill, or create a new library.</>
          )}
        </p>
      </div>
    );
  } else {
    guide = (
      <div className={styles.guide} data-testid="hub-library-guide-choose">
        <p className={styles.guideText}>
          Choose the library that holds this document&apos;s sources. Use › to look inside or add files.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.root} data-testid="hub-library-list">
      <div className={styles.header}>
        {workspaceSwitcher}
        <div className={styles.headerTop}>
          <h2 className={styles.headerTitle}>Source library</h2>
          {onCreateLibrary && (
            <button
              type="button"
              className={styles.createBtn}
              onClick={onCreateLibrary}
              aria-label="Create new library"
              data-testid="hub-library-create"
              title="Create new library"
            >
              + New library
            </button>
          )}
        </div>
        {guide}
        {libraries.length > 1 && (
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search libraries"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            data-testid="hub-library-search"
            aria-label="Search libraries"
          />
        )}
      </div>

      <div
        className={styles.listContainer}
        ref={listRef}
        onScroll={onScrollTopChange ? (e) => onScrollTopChange(e.currentTarget.scrollTop) : undefined}
      >
        {libraries.length === 0 ? (
          <div className={styles.empty} data-testid="hub-library-list-empty">
            No libraries in this workspace yet.
          </div>
        ) : filteredLibraries.length === 0 ? (
          <div className={styles.empty} data-testid="hub-library-list-empty">
            No libraries match your search.
          </div>
        ) : (
          <>
            {searchTerm.trim() && (
              <p className={styles.countLine}>
                {filteredLibraries.length} of {libraries.length} libraries
              </p>
            )}
            <div className={styles.list}>
              {filteredLibraries.map((lib) => {
                const isActive = lib.id === activeLibraryId;
                const empty = isEmptyLibrary(lib);
                return (
                  <div
                    key={lib.id}
                    className={styles.rowWrap}
                    aria-current={isActive ? "true" : undefined}
                    data-testid={`hub-library-list-row-${lib.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => onPickLibrary(lib.id)}
                      className={styles.rowBody}
                      aria-current={isActive ? "true" : undefined}
                      title={empty ? "Empty: press Upload to add documents" : undefined}
                      data-testid={`hub-library-list-row-body-${lib.id}`}
                    >
                      <span className={mergeClasses(styles.radio, isActive && styles.radioOn)} aria-hidden="true" />
                      <div className={styles.rowMain}>
                        <span className={styles.rowNameLine}>
                          <span className={mergeClasses(styles.rowName, isActive && styles.rowNameOn)} title={lib.name}>
                            {lib.name}
                          </span>
                          {lib.scope && <span className={styles.scopeTag}>{lib.scope}</span>}
                        </span>
                        {lib.description && (
                          <span
                            className={styles.rowSubtitle}
                            title={lib.description}
                            data-testid={`hub-library-list-info-${lib.id}`}
                          >
                            {lib.description}
                          </span>
                        )}
                        <span className={mergeClasses(styles.rowMeta, empty && styles.rowMetaEmpty)}>
                          {describeLib(lib, libraryPageCounts?.[lib.id])}
                        </span>
                      </div>
                    </button>
                    {empty && (
                      // Upload sits on the library it fills: it opens this
                      // library, where files are added.
                      <button
                        type="button"
                        aria-label={`Upload documents to ${lib.name}`}
                        data-testid={`hub-library-list-upload-${lib.id}`}
                        className={styles.rowUploadBtn}
                        onClick={() => onInspectLibrary(lib.id)}
                      >
                        Upload
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label={`Open ${lib.name}`}
                      title="Look inside"
                      data-testid={`hub-library-list-inspect-${lib.id}`}
                      className={styles.rowChevronBtn}
                      onClick={() => onInspectLibrary(lib.id)}
                    >
                      ›
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HubLibraryList;
